import { getAuth } from 'firebase/auth';
import { API_ENDPOINTS, type ApiEndpoint } from '../config/api';

// Helper function to get Firebase ID token
const getIdToken = async (): Promise<string | null> => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      console.warn('No authenticated user found');
      return null;
    }
    
    const token = await user.getIdToken();
    return token;
  } catch (error) {
    console.error('Error getting ID token:', error);
    return null;
  }
};

// Helper function to make authenticated API requests
export const authenticatedApiRequest = async (
  endpoint: ApiEndpoint,
  options: RequestInit = {}
): Promise<Response> => {
  const url = API_ENDPOINTS[endpoint];
  
  // Get the authentication token
  const token = await getIdToken();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers as Record<string, string>,
  };
  
  // Add authorization header if token is available
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const defaultOptions: RequestInit = {
    headers,
    ...options,
  };

  return fetch(url, defaultOptions);
};

// Helper function to make authenticated API requests with JSON body
export const authenticatedApiPost = async (
  endpoint: ApiEndpoint,
  body: any,
  options: RequestInit = {}
): Promise<Response> => {
  return authenticatedApiRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify(body),
    ...options,
  });
};

// Helper function to make authenticated API requests with GET method
export const authenticatedApiGet = async (
  endpoint: ApiEndpoint,
  options: RequestInit = {}
): Promise<Response> => {
  return authenticatedApiRequest(endpoint, {
    method: 'GET',
    ...options,
  });
};

// Helper function to make authenticated API requests with PUT method
export const authenticatedApiPut = async (
  endpoint: ApiEndpoint,
  body: any,
  options: RequestInit = {}
): Promise<Response> => {
  return authenticatedApiRequest(endpoint, {
    method: 'PUT',
    body: JSON.stringify(body),
    ...options,
  });
};

// Helper function to make authenticated API requests with DELETE method
export const authenticatedApiDelete = async (
  endpoint: ApiEndpoint,
  options: RequestInit = {}
): Promise<Response> => {
  return authenticatedApiRequest(endpoint, {
    method: 'DELETE',
    ...options,
  });
};
